import { useEffect, useState } from "react";
import { IdCard, CalendarCheck, MapPin } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { useAuth } from "../../../context/AuthContext";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { StepHeader } from "../../../components/kiosk/StepHeader";
import { StepProgress } from "../../../components/kiosk/StepProgress";
import { PhoneInput } from "../../../components/PhoneInput";
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { cacheGet, cacheSet } from "../../../lib/offlineCache";

const EMPTY_FORM = {
  visitor_name: "",
  visitor_phone: "",
  visitor_email: "",
  visitor_company: "",
  id_number: "",
  id_type: "",
  host_id: "",
  purpose: "",
  gate_id: "",
  invite_id: "",
};

const ID_TYPES = ["Aadhar Card", "PAN Card", "Passport", "Driving License", "Voter ID", "Company ID", "Other"];

export default function DetailsStep({ onNext, initialData }) {
  const { user } = useAuth();
  const [hosts, setHosts] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [gates, setGates] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(initialData || {}) });
  const [error, setError] = useState("");
  const initialPhone = initialData?.visitor_phone ? parsePhoneNumberFromString(initialData.visitor_phone) : null;
  const [phoneCountry, setPhoneCountry] = useState(initialPhone?.country || "IN");
  const [phoneNational, setPhoneNational] = useState(initialPhone?.nationalNumber || "");
  const [hostQuery, setHostQuery] = useState("");
  const [hostSuggestOpen, setHostSuggestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/users/hosts"), api.get("/purposes"), api.get("/gates")])
      .then(([h, p, g]) => {
        setHosts(h.data);
        setPurposes(p.data);
        setGates(g.data);
        cacheSet("hosts", h.data);
        cacheSet("purposes", p.data);
        cacheSet("gates", g.data);
        if (initialData?.host_id) {
          setHostQuery(h.data.find((host) => host.id === initialData.host_id)?.name || "");
        }
      })
      .catch(() => {
        const cached = cacheGet("hosts") || [];
        setHosts(cached);
        setPurposes(cacheGet("purposes"));
        setGates(cacheGet("gates"));
        if (initialData?.host_id) {
          setHostQuery(cached.find((host) => host.id === initialData.host_id)?.name || "");
        }
      });
  }, []);

  const filteredHosts = hosts.filter((h) => h.name.toLowerCase().includes(hostQuery.trim().toLowerCase()));

  useEffect(() => {
    if (user?.gate_id && !initialData?.gate_id) {
      setForm((f) => ({ ...f, gate_id: f.gate_id || user.gate_id }));
    }
  }, [user?.gate_id]);

  const handlePhoneCountryChange = (c) => {
    setPhoneCountry(c);
    setForm({ ...form, visitor_phone: phoneNational ? `+${getCountryCallingCode(c)}${phoneNational}` : "" });
  };

  const handlePhoneNationalChange = (digits) => {
    setPhoneNational(digits);
    setForm({ ...form, visitor_phone: digits ? `+${getCountryCallingCode(phoneCountry)}${digits}` : "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const phone = form.visitor_phone.trim();
    const email = form.visitor_email.trim();
    if (!phone && !email) {
      setError("Enter a phone number or an email — at least one is required");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address");
      return;
    }
    if (phone && !isValidPhoneNumber(phone)) {
      setError("Enter a valid phone number for the selected country");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await onNext(form);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not submit — please try again");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="checkin-details-step">
      <StepProgress current="details" />
      <StepHeader icon={IdCard} title="Visitor Details" subtitle="Confirm the information below" />
      {initialData?.invite_id && (
        <div className="border border-sky-200 bg-sky-50 rounded-lg p-4 flex items-center gap-3" data-testid="invite-selected-banner">
          <CalendarCheck className="text-brand-cyan shrink-0" size={20} />
          <p className="text-sm text-slate-700">Pre-registered visitor — details filled from the invite. Edit anything below if needed.</p>
        </div>
      )}
      <form onSubmit={submit} className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 sm:p-8 space-y-4">
        <Input
          data-testid="checkin-name-input"
          placeholder="Visitor full name"
          required
          value={form.visitor_name}
          onChange={(e) => setForm({ ...form, visitor_name: e.target.value })}
          className="h-14 rounded-sm bg-white border-slate-300 text-lg"
        />
        <PhoneInput
          testId="checkin-phone-input"
          country={phoneCountry}
          national={phoneNational}
          onCountryChange={handlePhoneCountryChange}
          onNationalChange={handlePhoneNationalChange}
          inputClassName="h-14 text-lg"
          triggerClassName="h-14"
        />
        <Input
          data-testid="checkin-email-input"
          placeholder="Email"
          type="text"
          inputMode="email"
          value={form.visitor_email}
          onChange={(e) => setForm({ ...form, visitor_email: e.target.value })}
          className="h-14 rounded-sm bg-white border-slate-300 text-lg"
        />
        <p className="text-xs text-slate-400 -mt-2">Provide at least a phone number or an email</p>
        <Input
          data-testid="checkin-company-input"
          placeholder="Company"
          required
          value={form.visitor_company}
          onChange={(e) => setForm({ ...form, visitor_company: e.target.value })}
          className="h-14 rounded-sm bg-white border-slate-300 text-lg"
        />
        <Select value={form.id_type} onValueChange={(v) => setForm({ ...form, id_type: v })}>
          <SelectTrigger data-testid="checkin-idtype-select" className="h-14 rounded-sm bg-white border-slate-300 text-lg">
            <SelectValue placeholder="ID Type" />
          </SelectTrigger>
          <SelectContent>
            {ID_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid="checkin-idnumber-input"
          placeholder="ID / passport number"
          required
          value={form.id_number}
          onChange={(e) => setForm({ ...form, id_number: e.target.value })}
          className="h-14 rounded-sm bg-white border-slate-300 text-lg"
        />
        <div className="relative">
          <Input
            data-testid="checkin-host-select"
            placeholder="Who are you visiting?"
            value={hostQuery}
            onChange={(e) => {
              setHostQuery(e.target.value);
              setHostSuggestOpen(true);
              if (form.host_id) setForm({ ...form, host_id: "" });
            }}
            onFocus={() => setHostSuggestOpen(true)}
            onBlur={() => setTimeout(() => setHostSuggestOpen(false), 150)}
            autoComplete="off"
            className="h-14 rounded-sm bg-white border-slate-300 text-lg"
          />
          {hostSuggestOpen && hostQuery.trim() && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 shadow-lg rounded-sm max-h-56 overflow-y-auto" data-testid="checkin-host-suggestions">
              {filteredHosts.length ? (
                filteredHosts.map((h) => (
                  <button
                    type="button"
                    key={h.id}
                    data-testid={`checkin-host-option-${h.id}`}
                    onMouseDown={() => {
                      setForm({ ...form, host_id: h.id });
                      setHostQuery(h.name);
                      setHostSuggestOpen(false);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-sky-50 text-slate-700 border-b border-slate-100 last:border-0"
                  >
                    {h.name}
                  </button>
                ))
              ) : (
                <p className="px-4 py-3 text-slate-400 text-sm" data-testid="checkin-host-no-results">
                  No host found
                </p>
              )}
            </div>
          )}
        </div>
        <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
          <SelectTrigger data-testid="checkin-purpose-select" className="h-14 rounded-sm bg-white border-slate-300 text-lg">
            <SelectValue placeholder="Purpose of visit" />
          </SelectTrigger>
          <SelectContent>
            {purposes.map((p) => (
              <SelectItem key={p.id} value={p.name}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {user?.gate_id ? (
          <div className="h-14 rounded-sm bg-slate-100 border border-slate-300 text-lg flex items-center px-4 text-slate-600 gap-2" data-testid="checkin-gate-locked">
            <MapPin size={18} className="text-brand-cyan shrink-0" />
            {gates.find((g) => g.id === form.gate_id)?.name || "Loading gate..."}
          </div>
        ) : (
          <Select value={form.gate_id} onValueChange={(v) => setForm({ ...form, gate_id: v })}>
            <SelectTrigger data-testid="checkin-gate-select" className="h-14 rounded-sm bg-white border-slate-300 text-lg">
              <SelectValue placeholder="Gate / Location" />
            </SelectTrigger>
            <SelectContent>
              {gates.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {error && (
          <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm" data-testid="checkin-details-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={!form.host_id || !form.purpose || !form.gate_id || !form.id_type || submitting}
          data-testid="checkin-details-continue"
          className="w-full h-16 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-lg"
        >
          {submitting ? "Submitting..." : "Continue"}
        </Button>
      </form>
    </div>
  );
}
